import { useEffect, useState } from "react";
import { Button, Checkbox, Input, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import YextApi, { yextErrorMessage } from "../../../api/YextApi";
import "./ProfileAmenitiesSection.scss";

const SERVICE_OPTIONS = [
  "Dine-in",
  "Takeout",
  "Delivery",
  "Curbside pickup",
  "Outdoor Seating",
];

const PRICE_RANGES = ["$", "$$", "$$$", "$$$$+"];
const ALCOHOL_OPTIONS = ["Beer", "Wine", "Cocktails", "None"];

const PARKING_OPTIONS = [
  "Free street parking",
  "Paid street parking",
  "Free parking lot",
  "Paid parking lot",
];

const ACCESSIBILITY_OPTIONS = [
  "Wheelchair-accessible entrance",
  "Wheelchair-accessible seating",
  "Wheelchair-accessible toilet",
  "Wheelchair-accessible car park",
];

const FAMILY_OPTIONS = ["Good for kids", "Kids' menu", "High chairs", "Changing tables"];

type YesNo = "yes" | "no" | null;

interface AmenitiesForm {
  cuisineTypes: string;
  serviceOptions: string[];
  reservationSupport: YesNo;
  orderingLinks: string[];
  priceRange: string | null;
  alcoholServed: string[];
  outdoorSeating: YesNo;
  wifiAvailable: YesNo;
  parking: string[];
  accessibility: string[];
  familyFriendly: string[];
}

const EMPTY: AmenitiesForm = {
  cuisineTypes: "",
  serviceOptions: [],
  reservationSupport: null,
  orderingLinks: [""],
  priceRange: null,
  alcoholServed: [],
  outdoorSeating: null,
  wifiAvailable: null,
  parking: [],
  accessibility: [],
  familyFriendly: [],
};

type MultiField = "serviceOptions" | "alcoholServed" | "parking" | "accessibility" | "familyFriendly";

interface Props {
  locationId?: number;
}

export function ProfileAmenitiesSection({ locationId }: Props) {
  const [form, setForm] = useState<AmenitiesForm>(EMPTY);
  const [savedSnapshot, setSavedSnapshot] = useState<AmenitiesForm>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      const a = data.amenities;
      if (!a) return;
      const loaded: AmenitiesForm = {
        cuisineTypes: a.cuisineTypes ?? "",
        serviceOptions: a.serviceOptions ?? [],
        reservationSupport: (a.reservationSupport as YesNo) ?? null,
        orderingLinks: a.orderingLinks && a.orderingLinks.length ? a.orderingLinks : [""],
        priceRange: a.priceRange ?? null,
        alcoholServed: a.alcoholServed ?? [],
        outdoorSeating: (a.outdoorSeating as YesNo) ?? null,
        wifiAvailable: (a.wifiAvailable as YesNo) ?? null,
        parking: a.parking ?? [],
        accessibility: a.accessibility ?? [],
        familyFriendly: a.familyFriendly ?? [],
      };
      setForm(loaded);
      setSavedSnapshot(loaded);
    });
  }, [locationId]);

  function update<K extends keyof AmenitiesForm>(key: K, value: AmenitiesForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleMulti(field: MultiField, value: string, checked: boolean) {
    setForm((f) => ({
      ...f,
      [field]: checked ? [...f[field], value] : f[field].filter((v) => v !== value),
    }));
  }

  function setOrderingLink(idx: number, value: string) {
    setForm((f) => ({
      ...f,
      orderingLinks: f.orderingLinks.map((l, i) => (i === idx ? value : l)),
    }));
  }

  function addOrderingLink() {
    setForm((f) => ({ ...f, orderingLinks: [...f.orderingLinks, ""] }));
  }

  function discardChanges() {
    setForm(savedSnapshot);
  }

  async function saveChanges() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setSaving(true);
    try {
      // Stored locally; amenities are not pushed to Yext yet (they need Yext custom fields).
      await YextApi.syncLocation(locationId, {
        amenities: {
          cuisineTypes: form.cuisineTypes,
          serviceOptions: form.serviceOptions,
          reservationSupport: form.reservationSupport ?? undefined,
          orderingLinks: form.orderingLinks.filter(Boolean),
          priceRange: form.priceRange ?? undefined,
          alcoholServed: form.alcoholServed,
          outdoorSeating: form.outdoorSeating ?? undefined,
          wifiAvailable: form.wifiAvailable ?? undefined,
          parking: form.parking,
          accessibility: form.accessibility,
          familyFriendly: form.familyFriendly,
        },
      });
      setSavedSnapshot(form);
      message.success("Amenities saved.");
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save amenities."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="profile-amenities">
      <h3 className="pa-title">Attributes & Amenities</h3>

      <Field label="Cuisine Types">
        <Input
          placeholder="Cuisine Types"
          size="large"
          value={form.cuisineTypes}
          onChange={(e) => update("cuisineTypes", e.target.value)}
        />
      </Field>

      <FieldGroup label="Service Options">
        <div className="pa-checkbox-grid pa-grid-3">
          {SERVICE_OPTIONS.map((opt) => (
            <Checkbox
              key={opt}
              checked={form.serviceOptions.includes(opt)}
              onChange={(e) => toggleMulti("serviceOptions", opt, e.target.checked)}
            >
              {opt}
            </Checkbox>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="Reservation Support">
        <div className="pa-checkbox-row">
          {(["yes", "no"] as const).map((v) => (
            <Checkbox
              key={v}
              checked={form.reservationSupport === v}
              onChange={(e) =>
                update("reservationSupport", e.target.checked ? v : null)
              }
            >
              {v === "yes" ? "Yes" : "No"}
            </Checkbox>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="Ordering Links">
        <div className="pa-ordering-links">
          {form.orderingLinks.map((link, idx) => (
            <Input
              key={idx}
              placeholder="Ordering Link"
              size="large"
              value={link}
              onChange={(e) => setOrderingLink(idx, e.target.value)}
            />
          ))}
          <Button
            className="pa-add-link-btn"
            icon={<PlusOutlined />}
            onClick={addOrderingLink}
          >
            Add Link
          </Button>
        </div>
      </FieldGroup>

      <FieldGroup label="Price Range">
        <div className="pa-checkbox-row">
          {PRICE_RANGES.map((p) => (
            <Checkbox
              key={p}
              checked={form.priceRange === p}
              onChange={(e) => update("priceRange", e.target.checked ? p : null)}
            >
              {p}
            </Checkbox>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="Alcohol Served">
        <div className="pa-checkbox-row">
          {ALCOHOL_OPTIONS.map((a) => (
            <Checkbox
              key={a}
              checked={form.alcoholServed.includes(a)}
              onChange={(e) => toggleMulti("alcoholServed", a, e.target.checked)}
            >
              {a}
            </Checkbox>
          ))}
        </div>
      </FieldGroup>

      <YesNoGroup
        label="Outdoor Seating"
        value={form.outdoorSeating}
        onChange={(v) => update("outdoorSeating", v)}
      />

      <YesNoGroup
        label="Wifi Available"
        value={form.wifiAvailable}
        onChange={(v) => update("wifiAvailable", v)}
      />

      <FieldGroup label="Parking Details">
        <div className="pa-checkbox-grid pa-grid-2">
          {PARKING_OPTIONS.map((p) => (
            <Checkbox
              key={p}
              checked={form.parking.includes(p)}
              onChange={(e) => toggleMulti("parking", p, e.target.checked)}
            >
              {p}
            </Checkbox>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="Accessibility Attributes">
        <div className="pa-checkbox-grid pa-grid-2">
          {ACCESSIBILITY_OPTIONS.map((a) => (
            <Checkbox
              key={a}
              checked={form.accessibility.includes(a)}
              onChange={(e) => toggleMulti("accessibility", a, e.target.checked)}
            >
              {a}
            </Checkbox>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="Family-Friendly Attributes">
        <div className="pa-checkbox-grid pa-grid-2">
          {FAMILY_OPTIONS.map((f) => (
            <Checkbox
              key={f}
              checked={form.familyFriendly.includes(f)}
              onChange={(e) => toggleMulti("familyFriendly", f, e.target.checked)}
            >
              {f}
            </Checkbox>
          ))}
        </div>
      </FieldGroup>

      <footer className="pa-actions">
        <Button className="pa-discard-btn" size="large" onClick={discardChanges} disabled={saving}>
          Discard Changes
        </Button>
        <Button
          type="primary"
          danger
          className="pa-save-btn"
          size="large"
          onClick={saveChanges}
          loading={saving}
        >
          Save Changes
        </Button>
      </footer>
    </div>
  );
}

interface YesNoGroupProps {
  label: string;
  value: YesNo;
  onChange: (v: YesNo) => void;
}

function YesNoGroup({ label, value, onChange }: YesNoGroupProps) {
  return (
    <FieldGroup label={label}>
      <div className="pa-checkbox-row">
        {(["yes", "no"] as const).map((v) => (
          <Checkbox
            key={v}
            checked={value === v}
            onChange={(e) => onChange(e.target.checked ? v : null)}
          >
            {v === "yes" ? "Yes" : "No"}
          </Checkbox>
        ))}
      </div>
    </FieldGroup>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="pa-field">
      <span className="pa-field-label">{label}</span>
      {children}
    </label>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="pa-field-group">
      <span className="pa-field-label">{label}</span>
      {children}
    </div>
  );
}
